// The shared vocabulary of the app: exercises, workouts and how they are counted.
// Used by the web app, the database seed and the session engine, so every part speaks about the same thing.
import type { DemoFamily } from './catalog/demo-families';

export type { DemoFamily };

/**
 * What an exercise needs. 'bodyweight' is strength work against your own body; 'none' is no load at all
 * (stretches, mobility, breathing). 'machine' covers stack and plate machines and the cardio machines (rower, bike, treadmill).
 */
export type Equipment =
  | 'kettlebell'
  | 'bodyweight'
  | 'dumbbell'
  | 'barbell'
  | 'none'
  | 'cable'
  | 'machine'
  | 'bench'
  | 'pullup_bar'
  | 'dip_bars'
  | 'band'
  | 'medicine_ball'
  | 'box'
  | 'jump_rope'
  | 'ab_wheel'
  | 'stability_ball'
  | 'bicycle'
  | 'skis'
  | 'inline_skates'
  | 'boat'
  | 'agility_ladder'
  | 'cones'
  | 'balance_board'
  | 'bosu'
  | 'ball';

/** Display names, for the library filter and the exercise page. */
export const EQUIPMENT_LABELS: Record<Equipment, string> = {
  kettlebell: 'Kettlebell',
  bodyweight: 'Bodyweight',
  dumbbell: 'Dumbbell',
  barbell: 'Barbell',
  none: 'No equipment',
  cable: 'Cable',
  machine: 'Machine',
  bench: 'Bench',
  pullup_bar: 'Pull-up bar',
  dip_bars: 'Dip bars',
  band: 'Resistance band',
  medicine_ball: 'Medicine ball',
  box: 'Box or step',
  jump_rope: 'Jump rope',
  ab_wheel: 'Ab wheel',
  stability_ball: 'Stability ball',
  bicycle: 'Bike',
  skis: 'Skis',
  inline_skates: 'Inline skates',
  boat: 'Rowing boat',
  agility_ladder: 'Agility ladder',
  cones: 'Cones',
  balance_board: 'Balance board',
  bosu: 'Bosu ball',
  ball: 'Small ball',
};

/** What kind of training an exercise is, for browsing and for building balanced workouts. */
export type ExerciseCategory = 'strength' | 'power' | 'plyometric' | 'cardio' | 'core' | 'mobility' | 'stretch' | 'breathing' | 'coordination';

/**
 * How an activity is trained and logged (decision log 2026-10-14). strength (the default): sets, reps, time;
 * cardio: endurance sports with distance, pace or speed, elevation and heart rate zones; mobility: stretches
 * and mobility; coordination: agility, balance, reaction and running drills. `activityKind()` in
 * packages/core/src/cardio derives it for exercises without the field.
 */
export type ActivityKind = 'strength' | 'cardio' | 'mobility' | 'coordination';

/** The endurance sports. Each has its own variants and fields (packages/core/src/cardio/sports.ts). */
export type CardioSport =
  | 'running'
  | 'walking'
  | 'hiking'
  | 'cycling'
  | 'mountain_biking'
  | 'swimming'
  | 'rowing'
  | 'elliptical'
  | 'skiing'
  | 'inline_skating'
  | 'stair_climbing'
  | 'jump_rope';

/** Heart rate zone, 1 (very easy) to 5 (all out), as a share of the maximum heart rate. */
export type HeartRateZone = 1 | 2 | 3 | 4 | 5;

/** The sport of a cardio exercise and its variant ("trail" for running, "indoor" for cycling). */
export interface ExerciseSport {
  sport: CardioSport;
  /** One of the sport's variants (CARDIO_SPORTS[sport].variants). */
  variant?: string;
  /** Indoors (treadmill, trainer, pool) or outdoors; undefined when either works. */
  indoor?: boolean;
}

/**
 * The cardio target of one block (one step of a cardio workout). All optional; units are always in the name.
 * A distance block still carries a time `amount` (the time at the target pace), so the session player can
 * run it as a timed step today; a player with GPS (build step 3) ends it on `distanceM` instead.
 */
export interface CardioTarget {
  /** work (the hard part), recovery (between repeats), easy (steady, warm-up, cool-down). Default: work. */
  role?: 'work' | 'recovery' | 'easy';
  /** How a recovery is done. */
  recovery?: 'jog' | 'walk' | 'rest' | 'easy_spin' | 'easy_row' | 'easy_swim';
  distanceM?: number;
  durationSec?: number;
  /** Target pace in seconds per km (running, walking, hiking, skiing); a range up to paceMaxSecPerKm. */
  paceSecPerKm?: number;
  paceMaxSecPerKm?: number;
  /** Target speed in km/h (cycling, inline skating). */
  speedKmh?: number;
  /** Target heart rate zone. */
  zone?: HeartRateZone;
  /** Target effort, 1 (very easy) to 10 (all out). */
  rpe?: number;
  /** Steps per minute (running) or pedal or stroke turns per minute (cycling, rowing). */
  cadence?: number;
  /** Climb per repeat in m (hill repeats) or over the block. */
  elevationM?: number;
}

/** How a cardio section is meant (the format stays list or circuit, so the session engine runs it as is). */
export type CardioStyle = 'steady' | 'intervals' | 'tempo' | 'fartlek' | 'long' | 'hill_repeats' | 'drills';

export interface SectionCardio {
  style: CardioStyle;
  sport?: CardioSport;
}

export type Level = 'beginner' | 'intermediate' | 'advanced';

/** Compound moves several joints (squat, row); isolation one (curl, leg extension). */
export type Mechanics = 'compound' | 'isolation';

/** The basic movement, so a workout can swap an exercise for another of the same pattern. */
export type MovementPattern =
  | 'squat'
  | 'hinge'
  | 'lunge'
  | 'horizontal_push'
  | 'vertical_push'
  | 'horizontal_pull'
  | 'vertical_pull'
  | 'carry'
  | 'core'
  | 'rotation'
  | 'locomotion'
  | 'jump'
  | 'full_body';

/** How a rep is counted: by the camera, by a timer (holds, stretches) or typed in by hand. */
export type CountingMode = 'camera' | 'time' | 'manual';

/**
 * How far camera counting is for an exercise:
 * - reliable: a counting pattern is assigned and every labeled test set of ours counted exactly
 * - experimental: a pattern is assigned (from WorkoutVision or an obvious equivalent) but not tested by us yet
 * - not_set_up: a front or side camera could count it, but there is no pattern yet (teach it first)
 */
export type CountingStatus = 'reliable' | 'experimental' | 'not_set_up';

/** Where the camera should stand. Front (or slightly diagonal) is the default so the screen works like a mirror. */
export type CameraView = 'front' | 'diagonal' | 'side' | 'any';

/**
 * How the camera counter did on public labeled data (packages/counter/bench, docs/research.md section 9): never a
 * reason for "reliable" (that needs our own recordings), meant for a later "experimental, tested on public data" line (not shown in the UI yet).
 */
export interface CountingBenchmark {
  /** The datasets and split, for example "MM-Fit + RepCount-pose, hold-out". */
  source: string;
  /** Labeled clips in the hold-out split. */
  clips: number;
  /** Share of those clips whose final count matched the label exactly, 0 to 1. */
  exactShare: number;
  /** When the benchmark ran, YYYY-MM-DD. */
  date: string;
}

/**
 * A counting setting taught on the person's own recordings (Teach the counter, apps/web/src/pages/TeachCounter.tsx).
 * Only the date is shared with the exercise; the counts and test sets stay in the person's private settings
 * (`UserSettings.teach` in @fitness/db).
 */
export interface TaughtCounting {
  /** When it was taught, YYYY-MM-DD. */
  date: string;
  /** The member who taught it, when an admin used their copy's counter for everyone and they agreed to be named. */
  by?: string;
}

/**
 * Camera exercises always carry a status; timed and manual ones never do. Reliable and experimental camera
 * exercises also carry the counter pattern ("joint/rest/first[/side][/lift]", WorkoutVision's format) and the
 * WorkoutVision entry it comes from (catalog/counter-patterns.ts).
 */
export type Counting =
  | { mode: 'camera'; status: CountingStatus; counterPattern?: string; counterRef?: string; benchmark?: CountingBenchmark; taught?: TaughtCounting }
  | { mode: 'time' }
  | { mode: 'manual' };

/** Effort for bounded XP: 1 light (stretches), 2 moderate, 3 hard. */
export type Intensity = 1 | 2 | 3;

/** Who made a library item and what it was copied from (shared library: only the creator changes the original). */
export interface Authorship {
  createdBy?: string;
  copiedFrom?: string;
}

export interface Exercise extends Authorship {
  /** Stable id, shared by demo, counting settings and history. ASCII, snake_case. */
  id: string;
  name: string;
  aliases?: string[];
  equipment: Equipment;
  muscles: string[];
  steps: string[];
  counting: Counting;
  camera: { view: CameraView; distanceM?: number };
  intensity: Intensity;
  /** Further equipment needed besides the main one, for example a bench for a dumbbell bench press. */
  extraEquipment?: Equipment[];
  category?: ExerciseCategory;
  level?: Level;
  mechanics?: Mechanics;
  pattern?: MovementPattern;
  /** One side at a time (single-arm press, lunge): amounts are usually given per side. */
  unilateral?: boolean;
  /** How it is trained and logged; derived from the category and sport when missing (activityKind()). */
  activity?: ActivityKind;
  /** Cardio exercises: the sport and its variant. */
  sport?: ExerciseSport;
  /**
   * 3D demo: the id of a motion clip on the shared figure (apps/web/src/demo/registry.ts lists them with source and
   * license). `opengym3d`: a free clip from OpenGym3D (Mesh2Motion, CC0); `own`: our own keyframed motion.
   * `recording`: made on the person's device from their own camera recording (body points only, never video); the
   * motion itself travels with the exercise. Null until one exists.
   */
  demo: { source: 'opengym3d' | 'own'; id: string } | RecordedDemo | null;
  /**
   * The movement family whose 3D motion stands in until the exercise has its own demo (catalog/demo-families.ts):
   * shown labeled "Similar movement". Exercises without one get a family from their pattern and category.
   */
  demoFamily?: DemoFamily;
  /** How the family motion is shown for this exercise: which equipment is in the hands, one arm, slower or faster. */
  demoProps?: DemoProps;
}

/** What the 3D figure holds: a kettlebell, dumbbells, a loaded barbell, a plain bar (cable, machine, band stand-in), a medicine ball. */
export type DemoPropKind = 'kettlebell' | 'dumbbell' | 'barbell' | 'bar' | 'ball';

/** Which hands hold it: both on one thing in the middle (C), one in each hand (LR), or one hand (L or R). */
export type DemoHold = 'C' | 'LR' | 'L' | 'R';

export interface DemoProps {
  /** Equipment to show instead of the one derived from the exercise; 'none' shows empty hands. */
  equipment?: DemoPropKind | 'none';
  /** Hold instead of the family's. */
  hold?: DemoHold;
  /** Only the right arm works; the left one rests (one-arm swing, single-arm press). */
  oneArm?: boolean;
  /** Playback speed, 1 = the family's pace (0.5 to 2). */
  tempo?: number;
}

/**
 * A motion clip on the shared 3D figure, as JSON (the format of apps/web/public/demos/clips/*.json): keyframes per
 * bone, sampled at `fps`, the last frame equal to the first so it loops.
 */
export interface DemoClip {
  v: 1;
  id: string;
  fps: number;
  frames: number;
  /** Fraction of the loop shown as the still pose (reduced motion). */
  still: number;
  /** Box around the figure over the whole clip, for the camera. */
  frame: { center: number[]; radius: number };
  /** `<bone>.quaternion` (x, y, z, w per frame), `root.position`, and `kettlebell.position` / `.quaternion` when one is held. */
  tracks: Record<string, number[]>;
  kettlebell?: boolean;
  /** Fixed apparatus drawn with the figure (pull-up bar, parallel bars, a seat, a wall): boxes, center and size in meters. */
  scene?: { at: number[]; size: number[] }[];
}

/** A 3D demo made from the person's own recording (apps/web/src/demo/retarget). */
export interface RecordedDemo {
  source: 'recording';
  id: string;
  /** Always "Own recording": the person filmed it themselves. */
  license: string;
  /** Day it was recorded, YYYY-MM-DD. */
  date: string;
  /** The session adds a side view (hinge, squat, lunge). */
  sideView: boolean;
  clip: DemoClip;
  /** The member who recorded it, when an admin used their copy's demo for everyone and they agreed to be named. */
  by?: string;
}

/** How much of an exercise: a number of reps or a time. perSide doubles it (left and right). */
export type Amount =
  | { kind: 'reps'; reps: number; repsMax?: number; perSide?: boolean }
  | { kind: 'time'; seconds: number; perSide?: boolean };

export interface Block {
  /** Stable within its workout, so history and rep data survive edits to the workout. */
  id: string;
  exerciseId: string;
  /** Required in list, sets, EMOM, AMRAP and continuous sections. In circuits the format's workSec applies; an amount here overrides it. */
  amount?: Amount;
  note?: string;
  /** Cardio blocks: distance, pace or speed, zone or effort, recovery. */
  cardio?: CardioTarget;
}

/**
 * How a section is run.
 * - list: each block once, one after the other (warm-up, cool-down)
 * - circuit: all blocks in turn for a number of rounds, each block for workSec, then restSec
 *   (also after the last block of a round), plus roundRestSec between rounds
 * - emom: every minute on the minute, blocks rotate
 * - amrap: as many rounds as possible in the time
 * - sets: each block for a number of sets, with rest between sets
 * - continuous: one clock runs for the minutes, without planned rest ("20 min kettlebell non-stop").
 *   The blocks are done in order and start over from the first until the time is up; each block's amount
 *   is the target for one pass (time blocks advance on their own, rep blocks when the reps are done).
 *   A single block fills the whole time. Unlike AMRAP the goal is a steady pace, not a round score;
 *   the session logs total reps and passes. Every block needs an amount, as in EMOM and AMRAP.
 */
export type SectionFormat =
  | { kind: 'list' }
  | { kind: 'circuit'; rounds: number; workSec: number; restSec: number; roundRestSec?: number }
  | { kind: 'emom'; minutes: number }
  | { kind: 'amrap'; minutes: number }
  | { kind: 'sets'; sets: number; restSec: number }
  | { kind: 'continuous'; minutes: number };

/** The main aim of a workout, for the library filter. */
export type WorkoutFocus = 'full_body' | 'upper_body' | 'lower_body' | 'core' | 'conditioning' | 'strength' | 'mobility' | 'endurance' | 'coordination';

export type SectionKind = 'warmup' | 'main' | 'cooldown';

export interface Section {
  /** Stable within its workout. */
  id: string;
  kind: SectionKind;
  format: SectionFormat;
  blocks: Block[];
  /** Cardio sections: steady, intervals, tempo, fartlek, long, hill repeats or drills. */
  cardio?: SectionCardio;
}

export interface Workout extends Authorship {
  id: string;
  name: string;
  equipment: Equipment[];
  /** Upper XP for a full session; the real XP is capped per day and week. */
  xp: number;
  /** One or two sentences for the library card. */
  description?: string;
  level?: Level;
  focus?: WorkoutFocus;
  source?: { label: string; url?: string };
  /**
   * The suggested warm-up and cool-down routines (packages/core/src/routines), by id. When set, the routine stands in
   * for any own section of that kind; the person can pick another routine or switch it off at the start
   * (withRoutines in workout.ts). Default workouts carry only their main part plus these ids.
   */
  warmupId?: string;
  cooldownId?: string;
  /** Cardio and coordination workouts; missing means strength. */
  activity?: ActivityKind;
  /** The main sport of a cardio workout. */
  sport?: CardioSport;
  sections: Section[];
}
